import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
    AlertTriangle, Plus, Search, Clock, CheckCircle, XCircle,
    Plane, MapPin, User, Shield, FileText, Timer, Ban, RefreshCw, History
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuditHistoryModal } from '../components/AuditHistoryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEL_CATEGORIES = [
    { value: 'A', label: 'Cat A — Segun MEL', days: null, description: 'Reparar dentro del intervalo especificado en el MEL', color: 'bg-red-500 text-white' },
    { value: 'B', label: 'Cat B — 3 dias', days: 3, description: '3 dias calendario para reparar', color: 'bg-orange-500 text-white' },
    { value: 'C', label: 'Cat C — 10 dias', days: 10, description: '10 dias calendario para reparar', color: 'bg-amber-500/10 text-amber-400' },
    { value: 'D', label: 'Cat D — 120 dias', days: 120, description: '120 dias calendario para reparar', color: 'bg-sky-500/10 text-sky-400' },
];

const emptyDeferral = {
    aircraft_id: '', mel_reference: '', description: '', category: 'B',
    ata_chapter: '', deferred_by: '', station_opened: '', station_repair: '',
    operational_limitations: '', placard_installed: false, custom_days: '',
    procedures_m: '', procedures_o: '', remarks: '',
};

export default function DeferralsPage() {
    const { hasRole, token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    // Auditoria 6 - Item 25: aceptar ?id= para highlight de diferido específico
    const [searchParams] = useSearchParams();
    const highlightId = searchParams.get('id');
    const [deferrals, setDeferrals] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('open');
    const [catFilter, setCatFilter] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [showClose, setShowClose] = useState(null);
    const [showOT, setShowOT] = useState(null);
    const [otHistory, setOtHistory] = useState([]);
    const [otForm, setOtForm] = useState({ title: '', description: '', maintenance_category: 'line', priority: 'normal' });
    const [otLoading, setOtLoading] = useState(false);
    const [auditItem, setAuditItem] = useState(null);
    const [closeForm, setCloseForm] = useState({ repair_notes: '', repair_station: '' });
    const [form, setForm] = useState(emptyDeferral);
    const canManage = hasRole(['admin', 'maintenance', 'ccm', 'dispatcher']);

    const fetchData = useCallback(async () => {
        try {
            const [dRes, aRes] = await Promise.all([
                axios.get(`${API}/deferrals`, { headers }),
                axios.get(`${API}/aircraft`, { headers }).catch(() => ({ data: [] })),
            ]);
            setDeferrals(dRes.data);
            setAircraft(aRes.data);
        } catch { toast.error('Error al cargar diferidos'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);
    // Auditoria 6 - Item 25: scroll al diferido si viene ?id=
    useEffect(() => {
        if (!highlightId || deferrals.length === 0) return;
        const el = document.getElementById(`deferral-card-${highlightId}`);
        if (el) {
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        }
    }, [highlightId, deferrals]);

    const createDeferral = async () => {
        if (!form.aircraft_id || !form.description || !form.mel_reference) {
            toast.error('Aeronave, referencia MEL y descripcion requeridos'); return;
        }
        const cat = MEL_CATEGORIES.find(c => c.value === form.category);
        let days = cat?.days;
        if (form.category === 'A' && form.custom_days) days = parseInt(form.custom_days);
        if (!days) days = 3;
        const dueDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
        try {
            const payload = { ...form, due_date: dueDate };
            delete payload.custom_days;
            await axios.post(`${API}/deferrals`, payload, { headers });
            toast.success('Diferido MEL creado');
            setShowCreate(false);
            setForm(emptyDeferral);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const closeDeferral = async () => {
        if (!showClose) return;
        try {
            await axios.patch(`${API}/deferrals/${showClose.id}/close`, closeForm, { headers });
            toast.success('Diferido cerrado — item reparado');
            setShowClose(null);
            setCloseForm({ repair_notes: '', repair_station: '' });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    // Paquete correcciones - Parte 3: cargar historial de OTs al abrir el dialog
    useEffect(() => {
        if (!showOT) { setOtHistory([]); setOtForm({ title: '', description: '', maintenance_category: 'line', priority: 'normal' }); return; }
        (async () => {
            try {
                const r = await axios.get(`${API}/deferrals/${showOT.id}/work-orders`, { headers });
                setOtHistory(r.data || []);
            } catch { setOtHistory([]); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOT]);

    const generateOT = async () => {
        if (!showOT) return;
        setOtLoading(true);
        try {
            const r = await axios.post(`${API}/deferrals/${showOT.id}/work-orders`, otForm, { headers });
            toast.success(`OT ${r.data.work_order.folio} creada`);
            // Refrescar historial local
            const list = await axios.get(`${API}/deferrals/${showOT.id}/work-orders`, { headers });
            setOtHistory(list.data || []);
            setOtForm({ title: '', description: '', maintenance_category: 'line', priority: 'normal' });
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al generar OT'); }
        finally { setOtLoading(false); }
    };

    const downloadOTPdf = async (mxId, folio) => {
        try {
            const r = await fetch(`${API}/maintenance/${mxId}/pdf`, { headers });
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `OT_${folio}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch { toast.error('Error al descargar PDF'); }
    };

    const getAc = (id) => aircraft.find(a => a.id === id);

    const filtered = deferrals.filter(d => {
        const ac = getAc(d.aircraft_id);
        const match = (d.mel_reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (d.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ac?.registration || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'all' || d.status === statusFilter;
        const matchCat = catFilter === 'all' || d.category === catFilter;
        return match && matchStatus && matchCat;
    });

    const openItems = deferrals.filter(d => d.status === 'open');
    const overdueItems = openItems.filter(d => d.is_overdue);
    const criticalItems = openItems.filter(d => d.days_remaining !== null && d.days_remaining <= 1 && !d.is_overdue);
    const closedItems = deferrals.filter(d => d.status === 'closed');

    if (loading) return <div className="space-y-4 animate-fade-in">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />)}</div>;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="deferrals-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Control de Diferidos (MEL/CDL)</h1>
                    <p className="text-muted-foreground">{openItems.length} diferidos activos — {overdueItems.length} vencidos</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={fetchData} data-testid="refresh-deferrals"><RefreshCw className="w-4 h-4 mr-1" />Actualizar</Button>
                    {canManage && <Button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="new-deferral-btn"><Plus className="w-4 h-4 mr-1" />Nuevo Diferido</Button>}
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-total-open">{openItems.length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Activos</p>
                </CardContent></Card>
                <Card className="card-aviation border-red-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-red-400" data-testid="kpi-overdue">{overdueItems.length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Vencidos</p>
                </CardContent></Card>
                <Card className="card-aviation border-orange-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-orange-400" data-testid="kpi-critical">{criticalItems.length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Criticos 24h</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-cat-b">{openItems.filter(d => d.category === 'B').length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Cat B (3d)</p>
                </CardContent></Card>
                <Card className="card-aviation border-emerald-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-emerald-400" data-testid="kpi-closed">{closedItems.length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Cerrados</p>
                </CardContent></Card>
            </div>

            {/* Overdue Alert */}
            {overdueItems.length > 0 && (
                <Card className="border-red-500/50 bg-red-500/5" data-testid="overdue-alert">
                    <CardContent className="p-4 flex items-center gap-3">
                        <Ban className="w-6 h-6 text-red-500 flex-shrink-0" />
                        <div>
                            <p className="font-bold text-red-400 text-sm">AERONAVE(S) CON DIFERIDOS VENCIDOS — NO PUEDE(N) VOLAR</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {overdueItems.map(d => getAc(d.aircraft_id)?.registration || '?').filter((v, i, a) => a.indexOf(v) === i).join(', ')} — Reparar inmediatamente o sacar de servicio
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar por MEL, descripcion, matricula..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-deferrals" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Abiertos</SelectItem>
                        <SelectItem value="closed">Cerrados</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={catFilter} onValueChange={setCatFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas Cat.</SelectItem>
                        {MEL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>Cat {c.value}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Deferrals List */}
            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No hay diferidos</p>
                    </CardContent></Card>
                ) : filtered.map(d => {
                    const ac = getAc(d.aircraft_id);
                    const cat = MEL_CATEGORIES.find(c => c.value === d.category);
                    const isOverdue = d.is_overdue;
                    const isCritical = d.days_remaining !== null && d.days_remaining <= 1 && !isOverdue;
                    return (
                        <Card key={d.id} id={`deferral-card-${d.id}`} className={`card-aviation ${d.id === highlightId ? 'ring-2 ring-primary shadow-lg' : ''} ${isOverdue ? 'border-red-500/60 bg-red-500/5 border-l-4 border-l-red-500' : isCritical ? 'border-orange-500/40 border-l-4 border-l-orange-500' : d.status === 'closed' ? 'opacity-60' : ''}`} data-testid={`deferral-${d.id}`}>
                            {isOverdue && <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/30 flex items-center gap-2">
                                <Ban className="w-3.5 h-3.5 text-red-500" /><span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Vencido — Aeronave No Puede Volar</span>
                            </div>}
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                            <Badge className={cat?.color || ''}> Cat {d.category}</Badge>
                                            {d.status === 'open' ? (
                                                d.days_remaining !== null ? (
                                                    <Badge className={isOverdue ? 'bg-red-500 text-white' : isCritical ? 'bg-orange-500 text-white' : d.days_remaining <= 3 ? 'bg-amber-500/10 text-amber-400' : 'bg-sky-500/10 text-sky-400'}>
                                                        {isOverdue ? 'VENCIDO' : `${d.days_remaining}d restantes`}
                                                    </Badge>
                                                ) : <Badge className="bg-amber-500/10 text-amber-400">Abierto</Badge>
                                            ) : (
                                                <Badge className="bg-emerald-500/10 text-emerald-400">Cerrado</Badge>
                                            )}
                                            <Badge variant="outline" className="text-[10px] font-mono">MEL {d.mel_reference}</Badge>
                                            {d.ata_chapter && <Badge variant="outline" className="text-[10px] font-mono">ATA {d.ata_chapter}</Badge>}
                                            {d.placard_installed && <Badge variant="outline" className="text-[10px]">INOP Placard</Badge>}
                                        </div>
                                        <p className="text-sm font-medium">{d.description}</p>
                                        {d.operational_limitations && (
                                            <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                                                <AlertTriangle className="w-3 h-3" />Limitaciones: {d.operational_limitations}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground flex-wrap">
                                            {ac && <span className="flex items-center gap-1"><Plane className="w-3 h-3" />{ac.registration}</span>}
                                            {d.deferred_by && <span className="flex items-center gap-1"><User className="w-3 h-3" />{d.deferred_by}</span>}
                                            {d.station_opened && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Abierto: {d.station_opened}</span>}
                                            {d.station_repair && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Reparar: {d.station_repair}</span>}
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(d.opened_date).toLocaleDateString('es')}</span>
                                            {d.due_date && <span className="font-mono">Vence: {new Date(d.due_date).toLocaleDateString('es')}</span>}
                                        </div>
                                    </div>
                                    {canManage && d.status === 'open' && (
                                        <Button size="sm" variant="outline" className="h-8 text-xs text-sky-500 border-sky-500/30 flex-shrink-0" onClick={() => setShowOT(d)} data-testid={`generate-ot-${d.id}`} title="Generar Orden de Trabajo">
                                            <FileText className="w-3.5 h-3.5 mr-1" />Generar OT
                                        </Button>
                                    )}
                                    {canManage && d.status === 'open' && (
                                        <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-500 border-emerald-500/30 flex-shrink-0" onClick={() => { setShowClose(d); setCloseForm({ repair_notes: '', repair_station: d.station_repair || '' }); }} data-testid={`close-deferral-${d.id}`}>
                                            <CheckCircle className="w-3.5 h-3.5 mr-1" />Cerrar
                                        </Button>
                                    )}
                                    <Button size="sm" variant="ghost" className="h-8 text-xs flex-shrink-0" onClick={() => setAuditItem(d)} data-testid={`audit-deferral-${d.id}`} title="Historial de cambios">
                                        <History className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Create Deferral Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="create-deferral-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Nuevo Diferido MEL</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Aeronave *</Label>
                                <Select value={form.aircraft_id} onValueChange={v => setForm({...form, aircraft_id: v})}>
                                    <SelectTrigger data-testid="def-aircraft"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{aircraft.map(ac => <SelectItem key={ac.id} value={ac.id}>{ac.registration} - {ac.aircraft_type}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Referencia MEL *</Label>
                                <Input placeholder="21-51-01" value={form.mel_reference} onChange={e => setForm({...form, mel_reference: e.target.value})} data-testid="def-mel" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Categoria MEL</Label>
                                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                                    <SelectTrigger data-testid="def-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>{MEL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <p className="text-[10px] text-muted-foreground">{MEL_CATEGORIES.find(c => c.value === form.category)?.description}</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Capitulo ATA</Label>
                                <Input placeholder="21, 32..." value={form.ata_chapter} onChange={e => setForm({...form, ata_chapter: e.target.value})} data-testid="def-ata" />
                            </div>
                        </div>
                        {form.category === 'A' && (
                            <div className="space-y-1.5 p-2 bg-red-500/5 rounded border border-red-500/20">
                                <Label className="text-xs text-red-400">Dias segun MEL (Cat A es variable)</Label>
                                <Input type="number" placeholder="Ej: 1, 5, 10" value={form.custom_days} onChange={e => setForm({...form, custom_days: e.target.value})} data-testid="def-custom-days" />
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Diferido por (Tecnico)</Label>
                                <Input placeholder="J. Ramirez" value={form.deferred_by} onChange={e => setForm({...form, deferred_by: e.target.value})} data-testid="def-by" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Estacion de Apertura</Label>
                                <Input placeholder="MGGT" maxLength={4} value={form.station_opened} onChange={e => setForm({...form, station_opened: e.target.value.toUpperCase()})} data-testid="def-station-open" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Estacion donde se Reparara</Label>
                            <Input placeholder="MGGT (base)" value={form.station_repair} onChange={e => setForm({...form, station_repair: e.target.value.toUpperCase()})} data-testid="def-station-repair" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Limitaciones Operacionales</Label>
                            <Input placeholder="Ej: Max FL250, pasajeros reducidos, sin Pack 1" value={form.operational_limitations} onChange={e => setForm({...form, operational_limitations: e.target.value})} data-testid="def-limitations" />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.placard_installed} onCheckedChange={v => setForm({...form, placard_installed: v})} data-testid="def-placard" />
                            <Label>Placard "INOP" instalado</Label>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Procedimiento (M)</Label><Input placeholder="Procedimiento mantenimiento" value={form.procedures_m} onChange={e => setForm({...form, procedures_m: e.target.value})} data-testid="def-proc-m" /></div>
                            <div className="space-y-1.5"><Label>Procedimiento (O)</Label><Input placeholder="Procedimiento operacional" value={form.procedures_o} onChange={e => setForm({...form, procedures_o: e.target.value})} data-testid="def-proc-o" /></div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Descripcion del Item Diferido *</Label>
                            <Textarea placeholder="Describe el item: que falla, condicion actual, acciones tomadas..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="def-desc" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Observaciones</Label>
                            <Textarea placeholder="Notas adicionales..." value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} data-testid="def-remarks" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createDeferral} className="btn-primary" data-testid="submit-deferral"><AlertTriangle className="w-4 h-4 mr-1" />Crear Diferido</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close Deferral Dialog */}
            <Dialog open={!!showClose} onOpenChange={() => setShowClose(null)}>
                <DialogContent className="sm:max-w-[400px]" data-testid="close-deferral-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Cerrar Diferido</DialogTitle></DialogHeader>
                    {showClose && (
                        <div className="space-y-3">
                            <div className="p-2 bg-muted/30 rounded text-xs">
                                <p className="font-bold">MEL {showClose.mel_reference}</p>
                                <p className="text-muted-foreground">{showClose.description}</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Estacion de Reparacion</Label>
                                <Input value={closeForm.repair_station} onChange={e => setCloseForm({...closeForm, repair_station: e.target.value.toUpperCase()})} placeholder="MGGT" data-testid="close-station" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Notas de Reparacion</Label>
                                <Textarea value={closeForm.repair_notes} onChange={e => setCloseForm({...closeForm, repair_notes: e.target.value})} placeholder="Que se hizo para resolver el diferido..." rows={3} data-testid="close-notes" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowClose(null)}>Cancelar</Button>
                        <Button onClick={closeDeferral} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-close-deferral">
                            <CheckCircle className="w-4 h-4 mr-1" />Cerrar Diferido
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FASE 2 UI: Historial de auditoria */}
            <AuditHistoryModal
                open={!!auditItem}
                onOpenChange={(o) => { if (!o) setAuditItem(null); }}
                collection="deferrals"
                entityId={auditItem?.id}
                entityLabel={auditItem ? `[${auditItem.category || 'B'}] ${auditItem.title || auditItem.description || ''}` : ''}
            />

            {/* Paquete correcciones - Parte 3: Generar OT dialog */}
            <Dialog open={!!showOT} onOpenChange={(o) => !o && setShowOT(null)}>
                <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="generate-ot-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                            <FileText className="w-5 h-5" /> Generar Orden de Trabajo
                        </DialogTitle>
                        {showOT && (
                            <p className="text-xs text-muted-foreground">
                                Diferido [{showOT.category || 'B'}] · {showOT.mel_ref || '(sin MEL)'} · vence {showOT.due_date ? new Date(showOT.due_date).toLocaleDateString('es') : '-'}
                            </p>
                        )}
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Categoría MX</Label>
                                <Select value={otForm.maintenance_category} onValueChange={v => setOtForm({...otForm, maintenance_category: v})}>
                                    <SelectTrigger data-testid="ot-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="line">Mantenimiento Línea</SelectItem>
                                        <SelectItem value="base">Mantenimiento Base</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Prioridad</Label>
                                <Select value={otForm.priority} onValueChange={v => setOtForm({...otForm, priority: v})}>
                                    <SelectTrigger data-testid="ot-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Baja</SelectItem>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="high">Alta</SelectItem>
                                        <SelectItem value="urgent">Urgente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Título (opcional)</Label>
                            <Input placeholder={`OT desde diferido MEL ${showOT?.mel_ref || ''}`} value={otForm.title} onChange={e => setOtForm({...otForm, title: e.target.value})} data-testid="ot-title" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Descripción del trabajo</Label>
                            <textarea rows={3} className="input-aviation w-full" placeholder={showOT?.description || 'Detalle del trabajo a ejecutar…'} value={otForm.description} onChange={e => setOtForm({...otForm, description: e.target.value})} data-testid="ot-description" />
                        </div>
                        <Button onClick={generateOT} disabled={otLoading} className="w-full" data-testid="ot-generate-btn">
                            {otLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                            Crear OT
                        </Button>

                        {otHistory.length > 0 && (
                            <div className="pt-2 border-t space-y-2" data-testid="ot-history">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Historial de OTs ({otHistory.length})</Label>
                                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                                    {otHistory.map(o => (
                                        <div key={o.id} className="flex items-center justify-between p-2 rounded border border-border/50">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold font-mono">{o.folio}</div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    {new Date(o.created_at).toLocaleString('es')} · {o.created_by_email || ''}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${o.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 text-slate-500'}`}>
                                                {o.status || 'scheduled'}
                                            </span>
                                            <Button size="sm" variant="ghost" className="h-7 text-[10px] ml-2" onClick={() => downloadOTPdf(o.id, o.folio)} data-testid={`ot-pdf-${o.folio}`}>
                                                PDF
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowOT(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
